// src/components/games/ReflexGame.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiRefreshCw, FiZap, FiAward } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const ReflexGame = ({ onBack }) => {
  const [gameState, setGameState] = useState('waiting'); // waiting, ready, click, result
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [times, setTimes] = useState([]);
  const [bestTime, setBestTime] = useState(() => {
    const saved = localStorage.getItem('reflexBestTime');
    return saved ? parseInt(saved) : null;
  });
  const [targetPosition, setTargetPosition] = useState({ x: 50, y: 50 });
  
  const startTimeRef = useRef(null);
  const timeoutRef = useRef(null);

  const maxAttempts = 5;

  const getRandomPosition = () => {
    return {
      x: Math.random() * 70 + 10, // 10% to 80%
      y: Math.random() * 60 + 15  // 15% to 75%
    };
  };

  const startRound = () => {
    setGameState('ready');
    setTargetPosition(getRandomPosition());
    
    // انتظر وقت عشوائي ثم اعرض الهدف
    const waitTime = Math.random() * 3000 + 1000; // 1-4 ثواني
    
    timeoutRef.current = setTimeout(() => {
      setGameState('click');
      startTimeRef.current = Date.now();
    }, waitTime);
  };

  const handleClick = () => {
    if (gameState === 'ready') {
      // ضغط مبكر جداً!
      toast.error('😅 انتظر حتى يظهر الهدف!');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setGameState('waiting');
      return;
    }

    if (gameState === 'click') {
      const reactionTime = Date.now() - startTimeRef.current;
      setCurrentTime(reactionTime);
      setTimes([...times, reactionTime]);
      setAttempts(attempts + 1);

      if (reactionTime < 300) {
        toast.success('🚀 سريع جداً!');
        setScore(score + 3);
      } else if (reactionTime < 500) {
        toast.success('⚡ سريع!');
        setScore(score + 2);
      } else {
        toast.success('✅ جيد!');
        setScore(score + 1);
      }

      if (attempts + 1 >= maxAttempts) {
        setGameState('result');
        const avgTime = Math.round([...times, reactionTime].reduce((a, b) => a + b, 0) / maxAttempts);
        
        if (!bestTime || avgTime < bestTime) {
          setBestTime(avgTime);
          localStorage.setItem('reflexBestTime', avgTime.toString());
          toast.success('🏆 رقم قياسي جديد!');
        }
      } else {
        setGameState('waiting');
      }
    }
  };

  const resetGame = () => {
    setGameState('waiting');
    setScore(0);
    setAttempts(0);
    setTimes([]);
    setCurrentTime(0);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (gameState === 'result') {
    const avgTime = Math.round(times.reduce((a, b) => a + b, 0) / times.length);
    const bestRound = Math.min(...times);

    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl border-2 border-yellow-100"
        >
          <div className="text-center">
            <div className="text-6xl mb-4">⚡</div>
            <h2 className="text-3xl font-black text-gray-900 mb-6">النتائج</h2>

            <div className="space-y-4 mb-6">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 border-2 border-yellow-200">
                <p className="text-sm font-bold text-gray-600 mb-1">متوسط وقت الاستجابة</p>
                <p className="text-3xl font-black text-yellow-600">{avgTime}ms</p>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200">
                <p className="text-sm font-bold text-gray-600 mb-1">أسرع ضغطة</p>
                <p className="text-3xl font-black text-green-600">{bestRound}ms</p>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border-2 border-blue-200">
                <p className="text-sm font-bold text-gray-600 mb-1">مجموع النقاط</p>
                <p className="text-3xl font-black text-blue-600">{score}</p>
              </div>

              {bestTime && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-200">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <FiAward className="text-purple-600" size={20} />
                    <p className="text-sm font-bold text-gray-600">أفضل متوسط سابق</p>
                  </div>
                  <p className="text-2xl font-black text-purple-600">{bestTime}ms</p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={resetGame}
                className="w-full bg-gradient-to-r from-yellow-500 to-amber-600 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition"
              >
                العب مرة أخرى
              </button>
              <button
                onClick={onBack}
                className="w-full bg-gray-100 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-200 transition"
              >
                رجوع
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 p-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-md hover:shadow-lg transition font-bold text-gray-700"
          >
            <FiArrowLeft size={18} />
            <span className="text-sm">رجوع</span>
          </button>

          <h1 className="text-2xl font-black text-gray-900">اختبار البديهة</h1>

          <button
            onClick={resetGame}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-md hover:shadow-lg transition font-bold text-gray-700"
          >
            <FiRefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-2xl mx-auto mb-6">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-yellow-200 text-center">
            <div className="text-2xl font-black text-yellow-600 mb-1">{attempts}/{maxAttempts}</div>
            <div className="text-xs font-bold text-gray-600">المحاولات</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-amber-200 text-center">
            <div className="text-2xl font-black text-amber-600 mb-1">{score}</div>
            <div className="text-xs font-bold text-gray-600">النقاط</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-orange-200 text-center">
            <div className="text-2xl font-black text-orange-600 mb-1">
              {currentTime > 0 ? `${currentTime}ms` : '--'}
            </div>
            <div className="text-xs font-bold text-gray-600">آخر وقت</div>
          </div>
        </div>
      </div>

      {/* Game Area */}
      <div className="max-w-4xl mx-auto">
        <div 
          onClick={handleClick}
          className={`relative w-full aspect-video rounded-3xl shadow-2xl border-4 overflow-hidden cursor-crosshair transition-all duration-300 ${
            gameState === 'waiting' 
              ? 'bg-white border-gray-200' 
              : gameState === 'ready' 
              ? 'bg-gradient-to-br from-red-500 to-pink-600 border-red-400 animate-pulse' 
              : 'bg-gradient-to-br from-green-500 to-emerald-600 border-green-400'
          }`}
        >
          {/* Instructions */}
          {gameState === 'waiting' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 flex flex-col items-center justify-center text-center p-8"
            >
              <FiZap className="text-yellow-500 mb-4" size={64} />
              <h3 className="text-3xl font-black text-gray-900 mb-3">استعد!</h3>
              <p className="text-gray-600 mb-6 max-w-md leading-relaxed">
                اضغط على الهدف الأصفر بأسرع ما يمكن عندما يظهر
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startRound();
                }}
                className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
              >
                ابدأ الجولة {attempts + 1}
              </button>
            </motion.div>
          )}

          {/* Ready State */}
          {gameState === 'ready' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: [0.8, 1, 0.8] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="text-white text-4xl font-black"
              >
                انتبه...
              </motion.div>
            </div>
          )}

          {/* Target */}
          <AnimatePresence>
            {gameState === 'click' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                style={{
                  position: 'absolute',
                  left: `${targetPosition.x}%`,
                  top: `${targetPosition.y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
                className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full shadow-2xl cursor-pointer flex items-center justify-center text-3xl sm:text-4xl animate-pulse"
              >
                🎯
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Tips */}
        <div className="mt-6 bg-white rounded-2xl p-4 shadow-md border-2 border-gray-100">
          <h4 className="font-black text-gray-900 mb-2 text-sm flex items-center gap-2">
            <FiZap className="text-yellow-500" />
            نصائح:
          </h4>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• أقل من 300ms = 3 نقاط ⚡</li>
            <li>• أقل من 500ms = 2 نقطة 🔥</li>
            <li>• أكثر من 500ms = 1 نقطة ✅</li>
            <li>• لا تضغط قبل ظهور الهدف!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ReflexGame;